<script setup lang="ts">
import { useToast } from 'vue-toastification';
import { ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { StudyPeriodSetup } from '@/types';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { queryKeys } from '@/plugins/store/actions/queries/study-periods/index';
import { i18n } from '@/plugins/internationalization/i18n';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';

interface Props {
    studyPeriodSetup: StudyPeriodSetup;
}

const props = defineProps<Props>();

const toast = useToast();

const isOpen = defineModel<boolean>('open', { required: true });

const isLoading = ref(false);

const queryClient = useQueryClient();

async function deleteStudyPeriodSetup() {
    try {
        if (isLoading.value) {
            return;
        }

        isLoading.value = true;

        await ApiClient.send('delete', `monitor/study-periods/setup/${props.studyPeriodSetup.id}`, {});

        queryClient.setQueryData(queryKeys.getStudyPeriodSetups(), (old: StudyPeriodSetup[] | undefined) => old?.filter((setup) => setup.id !== props.studyPeriodSetup.id));

        await queryClient.invalidateQueries({ queryKey: queryKeys.getStudyPeriodSetups() });

        toast.success(i18n.global.t('views.monitoring.teamComparison.dialogs.deleteSavedAnalysis.successMessage'));

        isOpen.value = false;
    } catch (err) {
        toast.error(i18n.global.t('views.monitoring.teamComparison.dialogs.deleteSavedAnalysis.errorMessage'));
    } finally {
        isLoading.value = false;
    }
}
</script>

<template>
    <Dialog v-model="isOpen" :disabled="isLoading">
        <DialogHeader>
            <DialogTitle>
                {{ $t('views.monitoring.teamComparison.dialogs.deleteSavedAnalysis.title') }}
            </DialogTitle>
            <DialogDescription class="mt-2 text-gray-500">
                {{ $t('views.monitoring.teamComparison.dialogs.deleteSavedAnalysis.description') }}
            </DialogDescription>
        </DialogHeader>
        <div class="p-4 rounded-md bg-gray-100">
            {{ studyPeriodSetup.name }}
        </div>
        <DialogFooter class="flex sm:justify-center pt-2">
            <DialogClose as-child>
                <CustomButton color-preset="white" class="min-w-44" :disabled="isLoading">{{ $t('views.monitoring.teamComparison.dialogs.deleteSavedAnalysis.buttons.cancel') }}</CustomButton>
            </DialogClose>
            <CustomButton color-preset="warning" class="min-w-44" :loading="isLoading" @click="deleteStudyPeriodSetup">
                {{ $t('views.monitoring.teamComparison.dialogs.deleteSavedAnalysis.buttons.delete') }}
            </CustomButton>
        </DialogFooter>
    </Dialog>
</template>
